import * as Sentry from '@sentry/react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { useEffect } from 'react';

export function initSentry() {
  // Só inicializa o Sentry em produção ou se explicitamente habilitado
  const isEnabled = import.meta.env.VITE_SENTRY_ENABLED === 'true' || import.meta.env.PROD;

  if (!isEnabled) {
    console.log('Sentry disabled in development');
    return;
  }

  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    console.warn('Sentry DSN not configured');
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,

    // Integrations
    integrations: [
      // Browser tracing para performance monitoring
      Sentry.browserTracingIntegration(),

      // Replay para session recording (opcional)
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),

      // React Router integration
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],

    // Performance Monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // 10% em prod, 100% em dev

    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% das sessões
    replaysOnErrorSampleRate: 1.0, // 100% quando há erro

    // Release tracking
    release: import.meta.env.VITE_APP_VERSION || 'development',

    // Before send - permite filtrar eventos antes de enviar
    beforeSend(event, hint) {
      // Não enviar erros de cancelamento de requisições
      if (hint.originalException instanceof Error) {
        if (hint.originalException.message.includes('Cancel')) {
          return null;
        }
      }
      return event;
    },

    // Ignorar erros específicos
    ignoreErrors: [
      // Erros de rede
      'NetworkError',
      'Network request failed',
      // Erros de extensões do navegador
      'Non-Error promise rejection captured',
      // Erros de ad blockers
      'ResizeObserver loop limit exceeded',
    ],
  });
}

// Helper para capturar erros manualmente
export function captureError(error: Error, context?: Record<string, unknown>) {
  Sentry.captureException(error, {
    extra: context,
  });
}

// Helper para capturar mensagens
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info') {
  Sentry.captureMessage(message, level);
}

// Helper para adicionar breadcrumb
export function addBreadcrumb(breadcrumb: Sentry.Breadcrumb) {
  Sentry.addBreadcrumb(breadcrumb);
}

// Helper para setar contexto do usuário
export function setUserContext(user: { id: string; email: string; name: string; role?: string }) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.name,
    role: user.role,
  });
}

// Helper para limpar contexto do usuário
export function clearUserContext() {
  Sentry.setUser(null);
}

// Helper para adicionar tags
export function setTag(key: string, value: string) {
  Sentry.setTag(key, value);
}

// Helper para adicionar contexto extra
export function setContext(name: string, context: Record<string, unknown>) {
  Sentry.setContext(name, context);
}

export { Sentry };
